import type { PaletteCommand } from "../types";

export const CommandMatch = {
  ExactTitle: 1,
  TitlePrefix: 0.9,
  TitleWords: 0.8,
  KeywordWords: 0.5,
  TitleInnerWords: 0.4,
  KeywordInnerWords: 0.3,
  None: 0,
} as const;

// Shorter search words would match inside nearly every word.
const MIN_INNER_MATCH_LENGTH = 3;

function toWords(text: string): string[] {
  return text
    .toLocaleLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

type WordMatcher = (word: string, searchWord: string) => boolean;

const startsWith: WordMatcher = (word, searchWord) => word.startsWith(searchWord);
const startsWithOrContains: WordMatcher = (word, searchWord) =>
  word.startsWith(searchWord) || (searchWord.length >= MIN_INNER_MATCH_LENGTH && word.includes(searchWord));

function everyWordMatches(searchWords: string[], words: string[], matches: WordMatcher): boolean {
  return searchWords.every((searchWord) => words.some((word) => matches(word, searchWord)));
}

export function scoreCommand(search: string, title: string, keywords: string[] = []): number {
  const searchWords = toWords(search);
  if (searchWords.length === 0) return CommandMatch.ExactTitle;

  const titleWords = toWords(title);
  const normalizedSearch = searchWords.join(" ");
  const normalizedTitle = titleWords.join(" ");

  if (normalizedTitle === normalizedSearch) return CommandMatch.ExactTitle;
  if (normalizedTitle.startsWith(normalizedSearch)) return CommandMatch.TitlePrefix;

  const allWords = [...titleWords, ...keywords.flatMap(toWords)];

  if (everyWordMatches(searchWords, titleWords, startsWith)) return CommandMatch.TitleWords;
  if (everyWordMatches(searchWords, allWords, startsWith)) return CommandMatch.KeywordWords;
  if (everyWordMatches(searchWords, titleWords, startsWithOrContains)) return CommandMatch.TitleInnerWords;
  if (everyWordMatches(searchWords, allWords, startsWithOrContains)) return CommandMatch.KeywordInnerWords;

  return CommandMatch.None;
}

// cmdk only passes an item's keywords to the filter, so the title has to travel as the first one.
export function toSearchTerms(command: PaletteCommand): string[] {
  return [command.title, command.subtitle ?? "", ...(command.keywords ?? [])];
}

export function scoreSearchTerms(search: string, [title = "", ...keywords]: string[] = []): number {
  return scoreCommand(search, title, keywords);
}
